/**
 * @file src/hooks/ory/useLoginFlow.tsx
 * @exports useLoginFlow
 * @summary Initializes and submits the Ory login flow.
 * @remarks Kratos answers a sent login code with a 400; that send is recorded per flow for the code page's analytics.
 */
import type { LoginFlow, Session, UiNode, UiText, UpdateLoginFlowBody } from "@ory/kratos-client";
import { useLocation } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { identity, pickBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { navigateApp } from "src/lib/app-router";
import { ORY_MESSAGE_MAPPING, paths } from "src/const";
import ory from "src/helpers/ory/sdk";
import { recordCodeSent } from "src/helpers/ory/verificationCodeAnalytics";
import {
	AxiosErrorType,
	handleFlowError,
	createRedirectionWithParams,
	noop,
	getInitialValues,
	redirectAndPreserveParams,
} from "src/helpers";
import { useGetReturnUrl } from "./useGetReturnUrl";
import { useQueryClient } from "@tanstack/react-query";

interface LoginFlowProps {
	noRedirect?: boolean;
	forceRefresh?: boolean;
}

/**
 * this hook initialize and manage login flow state
 * @returns { loading, flow, submitting, onSubmit, initalValues }
 */
export const useLoginFlow = ({ noRedirect, forceRefresh }: LoginFlowProps = {}) => {
	const location = useLocation();
	const searchParams = new URLSearchParams(location.searchStr);
	const queryClient = useQueryClient();

	const returnTo = useGetReturnUrl();

	const flowId = searchParams.get("flow");
	const email = searchParams.get("email");
	const voucherId = searchParams.get("voucherId");

	// AAL = Authorization Assurance Level. This implies that we want to upgrade the AAL, meaning that we want
	// to perform two-factor authentication/verification.
	const aal = searchParams.get("aal");
	// Refresh means we want to refresh the session. This is needed, for example, when we want to update the password
	// of a user.
	const refresh = searchParams.get("refresh");

	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [flow, setFlow] = useState<LoginFlow>();

	// initialize flow
	useEffect(() => {
		if (!true || flow) {
			return;
		}

		// initialize login flow or get existing flow
		const init = async (innerFlowId = "") => {
			if (innerFlowId) {
				try {
					setLoading(true);
					const { data } = await ory.getLoginFlow({ id: String(innerFlowId) });
					setFlow(data);
				} catch (error) {
					// if flow returns error then retry
					init().then(noop, noop);
				} finally {
					setLoading(false);
				}
			} else {
				try {
					setLoading(true);
					const { data } = await ory.createBrowserLoginFlow({
						refresh: forceRefresh ?? refresh === "true",
						aal: aal ? String(aal) : undefined,
						returnTo: returnTo ? String(returnTo) : undefined,
					});

					let query: { [key: string]: string } = {
						flow: data.id,
						return_to: returnTo,
						aal: aal as string,
						refresh: (forceRefresh ?? refresh) as string,
						email: String(email || "").toLowerCase(),
						voucherId: String(voucherId || ""),
					};
					// remove all falsy values
					query = pickBy(query, identity);
					if (!noRedirect) {
						// Keep the current auth page and avoid leaving a pre-flow login entry in browser history.
						navigateApp({ to: location.pathname, search: query, replace: true, skipRouteChange: true });
					}

					setFlow(data);
				} catch (error) {
					if (!noRedirect && error instanceof AxiosError) {
						// if session already available and we have returnTo url then redirec user to returnTo
						if (error?.response?.data.error?.id === "session_already_available" && returnTo) {
							const finalUrl = createRedirectionWithParams(returnTo);
							window.location.href = finalUrl as string;
							return;
						}

						if (error?.response?.data?.error?.code === 500) {
							window.location.href = paths.login;
							return;
						}
						handleFlowError(paths.login, setFlow)(error as AxiosError<AxiosErrorType>);
					}
				} finally {
					setLoading(false);
				}
			}
		};

		init(flowId as string).then(noop, noop);
	}, [flowId, true, returnTo, aal, refresh]);

	const proceedAfterLogin = useCallback(
		async (_session: Session) => {
			// bust the query cache on login, in case the previous session left stale data
			queryClient.resetQueries();

			if (flow?.return_to) {
				const finalUrl = createRedirectionWithParams(flow?.return_to);
				window.location.href = finalUrl;
				return;
			}
			return navigateApp({ href: paths.home });
		},
		[flow, queryClient],
	);

	const proceedTo2FA = useCallback(async () => {
		const aal2ReturnTo = flow?.return_to || returnTo;
		const shouldRefresh = forceRefresh ?? refresh === "true";
		const { data } = await ory.createBrowserLoginFlow({
			aal: "aal2",
			refresh: shouldRefresh,
			returnTo: aal2ReturnTo ? String(aal2ReturnTo) : undefined,
		});

		let query: { [key: string]: string } = {
			flow: data.id,
			return_to: aal2ReturnTo,
			aal: "aal2",
			refresh: shouldRefresh ? "true" : "",
			email: String(email || "").toLowerCase(),
			voucherId: String(voucherId || ""),
		};
		query = pickBy(query, identity);
		navigateApp({ to: location.pathname, search: query, replace: true, skipRouteChange: true });
		setFlow(data);
	}, [email, flow?.return_to, forceRefresh, refresh, returnTo, voucherId]);

	// call this function when form gets submitted
	const onSubmit = useCallback(
		async (values: UpdateLoginFlowBody) => {
			try {
				setSubmitting(true);
				if (!noRedirect) {
					try {
						// check whether user is already logged in.
						// this is special case for chrome browser as we have figured out
						// when user logs in first time and redirected to vms/cortex and if user press back button then user will lands on login page
						// and chrome browser is not re-rendering the page so hooks are not getting called.
						// so if we detect valid session then directly redirect user
						const session = await ory.toSession();
						return proceedAfterLogin(session?.data);
					} catch (error) {
						if (
							flow?.requested_aal !== "aal2" &&
							error instanceof AxiosError &&
							error.response?.data?.error?.id === "session_aal2_required"
						) {
							await proceedTo2FA();
							setSubmitting(false);
							return;
						}
						// No active session is expected here; continue with the current login flow.
					}
				}

				// do login api request
				const res = await ory.updateLoginFlow({
					flow: String(flow?.id),
					updateLoginFlowBody: values,
				});
				setSubmitting(false);

				if (!noRedirect) {
					// try to get session
					// if 2FA is required then it will throw "session_aal2_required" error and handleFlowError will handle the error and required user to login with aal2 parameter
					// if 2FA is not required then flow will continue as it is
					await ory.toSession();

					return proceedAfterLogin(res?.data?.session);
				}

				return res?.data;
			} catch (error) {
				setSubmitting(false);
				handleFlowError(paths.login, setFlow)(error as AxiosError<AxiosErrorType>);
				if (error instanceof AxiosError) {
					if (error.response?.data?.error?.id === "browser_location_change_required") {
						// Expected OIDC flow behavior: Kratos responds with redirect target in a 422 payload.
						// The redirect is already handled by handleFlowError, so we avoid surfacing a UI error.
						return;
					}
					if (error.response?.status === 400) {
						const messages = error?.response?.data?.ui?.messages as UiText[];
						// Apparently, even though the request is successful and the code email is sent, Kratos returns a 400 code
						// https://github.com/ory/kratos/issues/4052
						if (messages && values.method === "code") {
							const isEmailSent = messages?.find((item) => item.id === ORY_MESSAGE_MAPPING.LOGIN_EMAIL_CODE);
							if (isEmailSent) {
								recordCodeSent({ flowId: error.response?.data?.id });
							}
							if (isEmailSent && !noRedirect) {
								setFlow(error.response?.data);
								return redirectAndPreserveParams(paths.quickLoginCode, {
									flow: flowId,
									email: values.identifier,
								});
							}
						}

						setFlow(error.response?.data);
					}
					throw error.response?.data || error;
				}
			}
		},
		[flow, proceedAfterLogin, proceedTo2FA],
	);

	const initialValues = useMemo(() => {
		return getInitialValues(flow?.ui?.nodes as UiNode[]);
	}, [flow]);

	return useMemo(
		() => ({
			loading,
			flow,
			submitting,
			onSubmit,
			initialValues,
		}),
		[loading, flow, submitting, onSubmit, initialValues],
	);
};
